import React, { useEffect, useState } from 'react';
import { CChart } from '@coreui/react-chartjs';
import { CCard, CCardBody, CCardHeader, CCol, CRow } from '@coreui/react';
import { Layout } from '../../components/Layout';
import { Language } from '../../language/English';
import { StatsService, StatItem } from './Api/StatsService';
import { PAYMENT_METHOD_FEEDBACK_TYPE_TWINT, PAYMENT_METHOD_FEEDBACK_TYPE_APPLE_PAY, PAYMENT_METHOD_FEEDBACK_TYPE_GOOGLE_PAY } from './Api/StatsConstants';

const StatsChart = ({ 
    title, 
    description, 
    stats, 
    loading,
    error,
    label1 = Language.DOCUMENTS,
    label2 = Language.DRAFTS,
    isSingleSeries = false
}: { 
    title: string; 
    description: string; 
    stats: StatItem[] | any[]; 
    loading: boolean;
    error?: string | null;
    label1?: string;
    label2?: string;
    isSingleSeries?: boolean;
}) => {
    const labels = stats.map((item) => item.date);
    const paidData = stats.map((item: any) => item.paid !== undefined ? item.paid : item.count);
    const unpaidData = stats.map((item: any) => item.unpaid || 0);

    const datasets = isSingleSeries 
        ? [
            {
                label: label1,
                backgroundColor: '#003366',
                data: paidData,
                barPercentage: 1,
            }
        ]
        : [
            {
                label: label2,
                backgroundColor: '#87CEEB', 
                data: unpaidData,
                barPercentage: 1,
            },
            {
                label: label1,
                backgroundColor: '#003366',
                data: paidData,
                barPercentage: 1,
            },
        ];

    return (
        <CCard className="mb-4 shadow-sm">
            <CCardHeader className="bg-white py-3">
                <h5 className="mb-0">
                    <strong>{title}</strong> 
                    <span className="text-muted fw-normal ms-2">{description}</span>
                </h5>
            </CCardHeader>
            <CCardBody className="p-4">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{Language.LOADING_}</span>
                        </div>
                        <p className="mt-2 text-muted">{Language.LOADING_}</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-5">
                        <div className="text-danger mb-3">
                            <i className="cil-warning" style={{ fontSize: '2rem' }}></i>
                        </div>
                        <h6 className="text-danger">{error}</h6>
                        <p className="text-muted small">{Language.SOMETHING_IS_WRONG}</p>
                    </div>
                ) : (
                    <div style={{ height: '480px' }}>
                        <CChart
                            type="bar"
                            height={480}
                            data={{
                                labels: labels,
                                datasets: datasets,
                            }}
                            options={{
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: 'top',
                                    },
                                    tooltip: {
                                        enabled: true,
                                        position: 'average',
                                        mode: 'index',
                                        intersect: false,
                                        callbacks: {
                                            footer: (tooltipItems: any) => {
                                                let sum = 0;
                                                tooltipItems.forEach(function (tooltipItem: any) {
                                                    sum += (tooltipItem.raw || 0);
                                                });
                                                return Language.TOTAL + ': ' + sum;
                                            },
                                        },
                                    },
                                },
                                responsive: true,
                                scales: {
                                    x: {
                                        stacked: true,
                                        grid: {
                                            display: false,
                                        },
                                    },
                                    y: {
                                        stacked: true,
                                        beginAtZero: true,
                                        grid: {
                                            color: '#f0f0f0',
                                        },
                                        ticks: {
                                            
                                        },
                                    },
                                },
                            }}
                        />
                    </div>
                )}
            </CCardBody>
        </CCard>
    );
};

const Stats = () => {
    const [dailyDocStats, setDailyDocStats] = useState<StatItem[]>([]);
    const [monthlyDocStats, setMonthlyDocStats] = useState<StatItem[]>([]);
    const [dailyFilledDocStats, setDailyFilledDocStats] = useState<StatItem[]>([]);
    const [monthlyFilledDocStats, setMonthlyFilledDocStats] = useState<StatItem[]>([]);
    const [dailyTemplateDocStats, setDailyTemplateDocStats] = useState<StatItem[]>([]);
    const [monthlyTemplateDocStats, setMonthlyTemplateDocStats] = useState<StatItem[]>([]);
    const [dailyPayStats, setDailyPayStats] = useState<StatItem[]>([]);
    const [monthlyPayStats, setMonthlyPayStats] = useState<StatItem[]>([]);
    const [dailyUserStats, setDailyUserStats] = useState<StatItem[]>([]);
    const [monthlyUserStats, setMonthlyUserStats] = useState<StatItem[]>([]);
    
    const [dailyDocLoading, setDailyDocLoading] = useState(true);
    const [monthlyDocLoading, setMonthlyDocLoading] = useState(true);
    const [dailyFilledDocLoading, setDailyFilledDocLoading] = useState(true);
    const [monthlyFilledDocLoading, setMonthlyFilledDocLoading] = useState(true);
    const [dailyTemplateDocLoading, setDailyTemplateDocLoading] = useState(true);
    const [monthlyTemplateDocLoading, setMonthlyTemplateDocLoading] = useState(true);
    const [monthlyPayLoading, setMonthlyPayLoading] = useState<boolean>(true);
    const [monthlyPayError, setMonthlyPayError] = useState<string | null>(null);

    const [paymentMethodFeedbackStats, setPaymentMethodFeedbackStats] = useState<any[]>([]);
    const [paymentMethodFeedbackLoading, setPaymentMethodFeedbackLoading] = useState<boolean>(true);
    const [paymentMethodFeedbackError, setPaymentMethodFeedbackError] = useState<string | null>(null);

    const [dailyDocError, setDailyDocError] = useState<string | null>(null);
    const [dailyUserLoading, setDailyUserLoading] = useState<boolean>(true);
    const [dailyUserError, setDailyUserError] = useState<string | null>(null);
    const [monthlyDocError, setMonthlyDocError] = useState<string | null>(null);
    const [dailyFilledDocError, setDailyFilledDocError] = useState<string | null>(null);
    const [monthlyFilledDocError, setMonthlyFilledDocError] = useState<string | null>(null);
    const [dailyTemplateDocError, setDailyTemplateDocError] = useState<string | null>(null);
    const [monthlyTemplateDocError, setMonthlyTemplateDocError] = useState<string | null>(null);
    const [dailyPayLoading, setDailyPayLoading] = useState(true);
    const [monthlyUserLoading, setMonthlyUserLoading] = useState(true);

    const [dailyPayError, setDailyPayError] = useState<string | null>(null);
    const [monthlyUserError, setMonthlyUserError] = useState<string | null>(null);

    const statsService = new StatsService();

    useEffect(() => {
        const fetchStats = async () => {
            // Documents
            statsService.getDocumentStats().then(res => {
                if (res.isSuccess) {
                    setDailyDocStats(res.response);
                } else {
                    setDailyDocError(res.messageForUser);
                }
                setDailyDocLoading(false);
            }).catch(() => {
                setDailyDocError(Language.SOMETHING_IS_WRONG);
                setDailyDocLoading(false);
            });

            statsService.getDocumentMonthlyStats().then(res => {
                if (res.isSuccess) {
                    setMonthlyDocStats(res.response);
                } else {
                    setMonthlyDocError(res.messageForUser);
                }
                setMonthlyDocLoading(false);
            }).catch(() => {
                setMonthlyDocError(Language.SOMETHING_IS_WRONG);
                setMonthlyDocLoading(false);
            });

            // Filled Documents
            statsService.getFilledDocumentStats().then(res => {
                if (res.isSuccess) {
                    setDailyFilledDocStats(res.response);
                } else {
                    setDailyFilledDocError(res.messageForUser);
                }
                setDailyFilledDocLoading(false);
            }).catch(() => {
                setDailyFilledDocError(Language.SOMETHING_IS_WRONG);
                setDailyFilledDocLoading(false);
            });

            statsService.getFilledDocumentMonthlyStats().then(res => {
                if (res.isSuccess) {
                    setMonthlyFilledDocStats(res.response);
                } else {
                    setMonthlyFilledDocError(res.messageForUser);
                }
                setMonthlyFilledDocLoading(false);
            }).catch(() => {
                setMonthlyFilledDocError(Language.SOMETHING_IS_WRONG);
                setMonthlyFilledDocLoading(false);
            });

            // Template Only Not Filled Documents
            statsService.getTemplateDocumentStats().then(res => {
                if (res.isSuccess) {
                    setDailyTemplateDocStats(res.response);
                } else {
                    setDailyTemplateDocError(res.messageForUser);
                }
                setDailyTemplateDocLoading(false);
            }).catch(() => {
                setDailyTemplateDocError(Language.SOMETHING_IS_WRONG);
                setDailyTemplateDocLoading(false);
            });

            statsService.getTemplateDocumentMonthlyStats().then(res => {
                if (res.isSuccess) {
                    setMonthlyTemplateDocStats(res.response);
                } else {
                    setMonthlyTemplateDocError(res.messageForUser);
                }
                setMonthlyTemplateDocLoading(false);
            }).catch(() => {
                setMonthlyTemplateDocError(Language.SOMETHING_IS_WRONG);
                setMonthlyTemplateDocLoading(false);
            });

            // Payments
            statsService.getPaymentStats().then(res => {
                if (res.isSuccess) {
                    setDailyPayStats(res.response);
                } else {
                    setDailyPayError(res.messageForUser);
                }
                setDailyPayLoading(false);
            }).catch(() => {
                setDailyPayError(Language.SOMETHING_IS_WRONG);
                setDailyPayLoading(false);
            });

            statsService.getPaymentMonthlyStats().then(res => {
                if (res.isSuccess) {
                    setMonthlyPayStats(res.response);
                } else {
                    setMonthlyPayError(res.messageForUser);
                }
                setMonthlyPayLoading(false);
            }).catch(() => {
                setMonthlyPayError(Language.SOMETHING_IS_WRONG);
                setMonthlyPayLoading(false);
            });

            statsService.getPaymentMethodFeedbackStats().then(res => {
                if (res.isSuccess) {
                    setPaymentMethodFeedbackStats(res.response);
                } else {
                    setPaymentMethodFeedbackError(res.messageForUser);
                }
                setPaymentMethodFeedbackLoading(false);
            }).catch(() => {
                setPaymentMethodFeedbackError(Language.SOMETHING_IS_WRONG);
                setPaymentMethodFeedbackLoading(false);
            });

            // Users
            statsService.getUserStats().then(res => {
                if (res.isSuccess) {
                    setDailyUserStats(res.response);
                } else {
                    setDailyUserError(res.messageForUser);
                }
                setDailyUserLoading(false);
            }).catch(() => {
                setDailyUserError(Language.SOMETHING_IS_WRONG);
                setDailyUserLoading(false);
            });

            statsService.getUserMonthlyStats().then(res => {
                if (res.isSuccess) {
                    setMonthlyUserStats(res.response);
                } else {
                    setMonthlyUserError(res.messageForUser);
                }
                setMonthlyUserLoading(false);
            }).catch(() => {
                setMonthlyUserError(Language.SOMETHING_IS_WRONG);
                setMonthlyUserLoading(false);
            });
        };

        fetchStats();
    }, []);

    return (
        <Layout>
            <CRow>
                {/* Document Stats */}
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.DOCUMENTS} 
                        description={Language.LAST_40_DAYS_DRAFTS_VS_DOCUMENTS} 
                        stats={dailyDocStats} 
                        loading={dailyDocLoading} 
                        error={dailyDocError}
                    />
                </CCol>
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.DOCUMENTS} 
                        description={Language.LAST_14_MONTHS_DRAFTS_VS_DOCUMENTS} 
                        stats={monthlyDocStats} 
                        loading={monthlyDocLoading} 
                        error={monthlyDocError}
                    />
                </CCol>

                {/* Filled Document Stats */}
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.FILLED_DOCUMENTS} 
                        description={Language.LAST_40_DAYS_FILLED_DRAFTS_VS_DOCUMENTS} 
                        stats={dailyFilledDocStats} 
                        loading={dailyFilledDocLoading} 
                        error={dailyFilledDocError}
                        label1={Language.DOCUMENTS}
                        label2={Language.DRAFTS}
                    />
                </CCol>
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.FILLED_DOCUMENTS} 
                        description={Language.LAST_14_MONTHS_FILLED_DRAFTS_VS_DOCUMENTS} 
                        stats={monthlyFilledDocStats} 
                        loading={monthlyFilledDocLoading} 
                        error={monthlyFilledDocError}
                        label1={Language.DOCUMENTS}
                        label2={Language.DRAFTS}
                    />
                </CCol>

                {/* Template Only Not Filled Stats */}
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.TEMPLATE_ONLY_NOT_FILLED} 
                        description={Language.LAST_40_DAYS_TEMPLATE_ONLY_DRAFTS_VS_DOCUMENTS} 
                        stats={dailyTemplateDocStats} 
                        loading={dailyTemplateDocLoading} 
                        error={dailyTemplateDocError}
                        label1={Language.DOCUMENTS}
                        label2={Language.DRAFTS}
                    />
                </CCol>
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.TEMPLATE_ONLY_NOT_FILLED} 
                        description={Language.LAST_14_MONTHS_TEMPLATE_ONLY_DRAFTS_VS_DOCUMENTS} 
                        stats={monthlyTemplateDocStats} 
                        loading={monthlyTemplateDocLoading} 
                        error={monthlyTemplateDocError}
                        label1={Language.DOCUMENTS}
                        label2={Language.DRAFTS}
                    />
                </CCol>

                {/* Payment Stats */}
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.PAYMENTS} 
                        description={Language.LAST_40_DAYS_PAYMENT_SUCCESS_VS_NOT_COMPLETED} 
                        stats={dailyPayStats} 
                        loading={dailyPayLoading}
                        error={dailyPayError}
                        label1={Language.PAYMENT_SUCCESS}
                        label2={Language.PAYMENT_NOT_COMPLETED} 
                    />
                </CCol>
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.PAYMENTS} 
                        description={Language.LAST_14_MONTHS_PAYMENT_SUCCESS_VS_NOT_COMPLETED} 
                        stats={monthlyPayStats} 
                        loading={monthlyPayLoading}
                        error={monthlyPayError}
                        label1={Language.PAYMENT_SUCCESS}
                        label2={Language.PAYMENT_NOT_COMPLETED} 
                    />
                </CCol>

                {/* User Stats */}
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.USERS} 
                        description={Language.NEW_USERS_LAST_40_DAYS} 
                        stats={dailyUserStats} 
                        loading={dailyUserLoading}
                        error={dailyUserError}
                        label1={Language.USERS}
                        isSingleSeries={true}
                    />
                </CCol>
                <CCol xs={12}>
                    <StatsChart 
                        title={Language.USERS} 
                        description={Language.NEW_USERS_LAST_14_MONTHS} 
                        stats={monthlyUserStats} 
                        loading={monthlyUserLoading}
                        error={monthlyUserError}
                        label1={Language.USERS}
                        isSingleSeries={true}
                    />
                </CCol>

                {/* Payment Method Requests */}
                <CCol xs={12}>
                    <CCard className="mb-4">
                        <CCardHeader>
                            <strong>{Language.PAYMENT_METHOD_REQUESTS}</strong>
                        </CCardHeader>
                        <CCardBody>
                            {paymentMethodFeedbackLoading ? (
                                <p>{Language.LOADING_}</p>
                            ) : paymentMethodFeedbackError ? (
                                <p className="text-danger">{paymentMethodFeedbackError}</p>
                            ) : (
                                <ul style={{ listStyleType: 'none', padding: 0 }}>
                                    <li style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}><strong>{Language.TWINT}:</strong> {paymentMethodFeedbackStats[PAYMENT_METHOD_FEEDBACK_TYPE_TWINT] || 0} {Language.REQUESTS}</li>
                                    <li style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}><strong>{Language.APPLE_PAY}:</strong> {paymentMethodFeedbackStats[PAYMENT_METHOD_FEEDBACK_TYPE_APPLE_PAY] || 0} {Language.REQUESTS}</li>
                                    <li style={{ padding: '8px 0' }}><strong>{Language.GOOGLE_PAY}:</strong> {paymentMethodFeedbackStats[PAYMENT_METHOD_FEEDBACK_TYPE_GOOGLE_PAY] || 0} {Language.REQUESTS}</li>
                                </ul>
                            )}
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>
        </Layout>
    );
};

export default Stats;
